import { getCurrentPage, getDynamicData, isComponentType } from '@avensia/excite';
import {
  send,
  addLineItem as exciteAddLineItem,
  removeLineItem as exciteRemoveLineItem,
  addPackage as exciteAddPackage,
  changeLineItemQuantity as exciteChangeLineItemQuantity,
  isCompanyCustomer,
  setCustomerTypeOnCart,
} from '@avensia/excite-commercetools';
import { type CartCommandEventData } from '@avensia/excite-commercetools/cart/command';

export function setPartialDelivery(value: boolean) {
  const checkout = getDynamicData<Excite.Commercetools.CheckoutApiModel>();

  const command: NordicNest.Api.SetPartialDeliveryCustomFieldCommand = {
    $type: 'SetPartialDeliveryCustomFieldCommand',
    resolveCheckoutData: true,
    value,
  };

  checkout.update(current => ({
    ...current,
    viewData: current.viewData && { ...current.viewData, partialDelivery: value ?? false },
  }));

  return send(command);
}

type CartUpdateOptions = {
  resolveCheckoutData?: boolean;
  eventData?: CartCommandEventData;
};

export async function addPackage(
  packageKey: string,
  packageParts: Excite.Commercetools.PackagePartRequest[],
  quantity: number = 1,
  { resolveCheckoutData = isCheckoutPage(), eventData }: CartUpdateOptions = {},
) {
  return exciteAddPackage(packageKey, packageParts, quantity, { eventData, resolveCheckoutData });
}

export async function changeLineItemQuantity(
  lineItemId: string,
  quantity: number,
  { resolveCheckoutData = isCheckoutPage(), eventData }: CartUpdateOptions = {},
) {
  return exciteChangeLineItemQuantity(lineItemId, quantity, { resolveCheckoutData, eventData }, undefined, 'CustomChangeLineItemQuantityCommand');
}

export function removeLineItem(
  lineItemId: string,
  { resolveCheckoutData = isCheckoutPage(), eventData }: CartUpdateOptions = {},
) {
  return exciteRemoveLineItem(lineItemId, { resolveCheckoutData, eventData }, undefined, 'CustomChangeLineItemQuantityCommand');
}

export function addLineItem(
  sku: string,
  quantity = 1,
  { resolveCheckoutData = isCheckoutPage(), eventData }: CartUpdateOptions = {},
) {
  if (isCompanyCustomer()) {
    setCustomerTypeOnCart('company');
  }

  return exciteAddLineItem(
    sku,
    quantity,
    {
      resolveCheckoutData,
      eventData,
    },
    undefined,
    'NordicnestAddLineItemCommand',
  );
}
export function setShippingMethod(id: string, method?: Excite.Commercetools.ShippingMethodApiModel) {
  const checkout = getDynamicData<Excite.Commercetools.CheckoutApiModel>();

  const command: NordicNest.Api.NordicNestSetShippingMethodCommand = {
    $type: 'NordicNestSetShippingMethodCommand',
    resolveCheckoutData: true,
    id,
  };

  checkout.update(current => ({ ...current, selectedShippingMethodId: id ?? '' }));

  return send(command, {
    additionalProductAttributes: {
      ecommerce: {
        selected_shipping_method: method?.viewData?.key,
        selected_shipping_price: method?.price?.inclVat,
      },
    },
  });
}

export function setShippingServicePointMethod(id: string) {
  const checkout = getDynamicData<Excite.Commercetools.CheckoutApiModel>();

  const command: NordicNest.Api.SetShippingServicePointCommand = {
    $type: 'SetShippingServicePointCommand',
    resolveCheckoutData: true,
    id,
  };

  checkout.update(current => ({
    ...current,
    viewData: current.viewData && { ...current.viewData, selectedAgentId: id },
  }));

  return send(command);
}

export function setCustomerSsnCustomFieldCommand(value: string) {
  const checkout = getDynamicData<Excite.Commercetools.CheckoutApiModel>();

  const command: NordicNest.Api.SetCustomerSsnCustomFieldCommand = {
    $type: 'SetCustomerSsnCustomFieldCommand',
    resolveCheckoutData: true,
    value,
  };

  checkout.update(current => ({
    ...current,
    viewData: current.viewData && { ...current.viewData, customerSsn: value },
  }));

  return send(command);
}

export async function setSelectedPaymentMethod(value: string) {
  const command: NordicNest.Api.SetSelectedPaymentMethodCommand = {
    $type: 'SetSelectedPaymentMethodCommand',
    resolveCheckoutData: true,
    value,
  };

  return send(command);
}

export function setPriorityDelivery(value: boolean) {
  const checkout = getDynamicData<Excite.Commercetools.CheckoutApiModel>();

  const command: NordicNest.Api.SetPriorityDeliveryCustomFieldCommand = {
    $type: 'SetPriorityDeliveryCustomFieldCommand',
    resolveCheckoutData: true,
    value,
  };

  checkout.update(current => ({
    ...current,
    viewData: current.viewData && { ...current.viewData, priorityDelivery: value ?? false },
  }));

  return send(command);
}

export async function addQuoteToCart(quoteId: string) {
  return new Promise<string>(async (resolve, reject) => {
    try {
      const response = (await send({
        $type: 'AddQuoteToCartCommand',
        quoteId,
        resolveCheckoutData: true,
      } as Excite.Quotes.AddQuoteToCartCommand)) as Excite.Commercetools.CartValidationApiModel;
      if (response.hasErrors) {
        reject(response.validationErrors[0]);
      } else {
        resolve('');
      }
    } catch (e) {
      reject();
    }
  });
}

export async function removeQuoteFromCart(quoteId: string) {
  return new Promise<string>(async (resolve, reject) => {
    try {
      const response = (await send({
        $type: 'RemoveQuoteFromCart',
        quoteId,
        resolveCheckoutData: true,
      } as Excite.Quotes.RemoveQuoteFromCart)) as Excite.Commercetools.CartValidationApiModel;
      if (response.hasErrors) {
        reject(response.validationErrors[0]);
      } else {
        resolve('');
      }
    } catch (e) {
      reject();
    }
  });
}

function isCheckoutPage() {
  return isComponentType<NordicNest.Api.CheckoutPageApiModel>(getCurrentPage());
}

export function addLineItemWithProductConfiguration(
  sku: string,
  selectedConfiguration: { [item: string]: string },
  quantity: number = 1,
) {
  const command: NordicNest.Api.AddLineItemConfigCommand = {
    $type: 'AddLineItemConfigCommand',
    resolveCheckoutData: isCheckoutPage(),
    quantity,
    sku,
    selectedConfiguration,
  };

  return send(command);
}


export function addBundle(
  bundleKey: string,
  quantity: number = 1,
  { resolveCheckoutData = isCheckoutPage(), eventData }: CartUpdateOptions = {},
  customCartCommand?: string,
) {

  return send(
    {
      $type: customCartCommand ?? 'AddBundleCommand',
      bundleKey,
      quantity,
      resolveCheckoutData,
      trackingType: 'AddLineItem',
    },
    eventData,
  );
}
